import React, { useState, useEffect, useRef } from 'react';
import nextlaneApi from '../services/nextlane';

function NextlaneSearch() {
  const [concessions, setConcessions] = useState([]);
  const [selectedConcession, setSelectedConcession] = useState('all');
  const [mode, setMode] = useState('immat');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const debounceRef = useRef(null);
  const abortRef = useRef(null);   // annule les requêtes en vol au changement de texte
  const seqRef = useRef(0);        // ignore les réponses obsolètes

  useEffect(() => {
    nextlaneApi.getConcessions().then(r => setConcessions(r.data)).catch(() => {});
  }, []);

  // Recherche LIVE en éventail : une requête par concession, rendu progressif.
  const doSearch = (q, dealershipId, m) => {
    const query = (q || '').trim();
    if (query.length < 3) {
      if (abortRef.current) abortRef.current.abort();
      setResults([]); setSearched(false); setLoading(false);
      return;
    }
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const seq = ++seqRef.current;

    const targets = (dealershipId && dealershipId !== 'all')
      ? concessions.filter(c => c.id === dealershipId)
      : concessions;
    if (targets.length === 0) return;

    setResults([]);
    setSearched(true);
    setLoading(true);
    let pending = targets.length;

    targets.forEach(async (c) => {
      try {
        const { data } = await nextlaneApi.search(query, m, c.id, controller.signal);
        if (seq !== seqRef.current) return;
        const group = (data.results || [])[0];
        if (group && group.matches?.length) {
          setResults(prev => [
            ...prev.filter(g => g.dealership?.id !== c.id),
            group,
          ].sort((a, b) => (a.dealership?.name || '').localeCompare(b.dealership?.name || '')));
        }
      } catch { /* abort ou erreur — on ignore */ }
      finally {
        if (seq === seqRef.current) {
          pending -= 1;
          if (pending <= 0) setLoading(false);
        }
      }
    });
  };

  const handleQueryChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(val, selectedConcession, mode), 350);
  };

  const handleConcessionChange = (e) => {
    const val = e.target.value;
    setSelectedConcession(val);
    if (query.trim().length >= 3) doSearch(query, val, mode);
  };

  const handleModeChange = (m) => {
    setMode(m);
    setResults([]);
    if (query.trim().length >= 3) doSearch(query, selectedConcession, m);
  };

  const totalMatches = results.reduce((sum, r) => sum + (r.matches?.length || 0), 0);

  return (
    <div className="page-container">
      <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>
        Recherche en direct dans Datacar (Nextlane) — toutes concessions, données temps réel
      </div>

      <div className="page-toolbar">
        <div style={{ display: 'flex', border: '1px solid var(--border-default)', borderRadius: '6px', overflow: 'hidden', flexShrink: 0 }}>
          <button
            style={{ border: 'none', padding: '6px 12px', fontSize: '12px', fontWeight: mode === 'immat' ? 600 : 400, cursor: 'pointer', background: mode === 'immat' ? 'var(--color-accent)' : 'var(--bg-input)', color: mode === 'immat' ? '#fff' : 'var(--text-secondary)' }}
            onClick={() => handleModeChange('immat')}
          >Immat</button>
          <button
            style={{ border: 'none', borderLeft: '1px solid var(--border-default)', padding: '6px 12px', fontSize: '12px', fontWeight: mode === 'nom' ? 600 : 400, cursor: 'pointer', background: mode === 'nom' ? 'var(--color-accent)' : 'var(--bg-input)', color: mode === 'nom' ? '#fff' : 'var(--text-secondary)' }}
            onClick={() => handleModeChange('nom')}
          >Nom client</button>
        </div>
        <input
          className="page-search"
          placeholder={mode === 'immat' ? '🔍 Rechercher par immatriculation...' : '🔍 Rechercher par nom client...'}
          value={query}
          onChange={handleQueryChange}
        />
        <select
          className={`page-select ${selectedConcession !== 'all' ? 'active-filter' : ''}`}
          value={selectedConcession}
          onChange={handleConcessionChange}
        >
          <option value="all">Toutes les concessions</option>
          {concessions.map(c => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
      </div>

      {query.trim().length > 0 && query.trim().length < 3 && (
        <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-muted)' }}>
          Saisissez au moins 3 caractères…
        </div>
      )}

      {totalMatches > 0 && (
        <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '4px' }}>
          {totalMatches} résultat{totalMatches > 1 ? 's' : ''}
        </div>
      )}

      {/* Rendu progressif : chaque concession s'affiche dès qu'elle répond */}
      {results.map((group) => {
        if (!group.matches?.length) return null;
        return (
          <div key={group.dealership?.id} style={{ marginBottom: '16px' }}>
            <div style={{
              fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)',
              padding: '6px 12px', background: 'var(--bg-muted)', borderRadius: '6px', marginBottom: '8px'
            }}>
              {group.dealership?.name} — {group.matches.length} résultat{group.matches.length > 1 ? 's' : ''}
            </div>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Immat</th>
                    <th>Marque / Modèle</th>
                    <th>VIN</th>
                    <th>Km</th>
                    <th>Client</th>
                    <th>Tél</th>
                    <th>Email</th>
                  </tr>
                </thead>
                <tbody>
                  {group.matches.map(({ vehicle, client }, idx) => (
                    <tr key={(vehicle?.InternalVehID || vehicle?.VIN || client?.InternalClientID || '') + '-' + idx}>
                      <td style={{ fontWeight: 600 }}>{vehicle?.RegistrationNumber || '—'}</td>
                      <td>{vehicle ? `${vehicle.BrandDescr || ''} ${(vehicle.ModelDescr || '').replace(/^\*\s?/, '')}` : '—'}</td>
                      <td style={{ fontFamily: 'monospace', fontSize: '11px' }}>{vehicle?.VIN || '—'}</td>
                      <td>{vehicle?.MileageValue ? `${Number(vehicle.MileageValue).toLocaleString('fr-FR')} km` : '—'}</td>
                      <td>{client ? `${client.FirstName} ${client.LastName}` : '—'}</td>
                      <td>
                        {Array.isArray(client?.Phones) && client.Phones.length > 0
                          ? client.Phones.map((p, i) => (
                              <div key={i} style={{ whiteSpace: 'nowrap' }}>
                                {p.value}
                                {p.usage && <span style={{ color: 'var(--text-muted)', fontSize: 11 }}> {p.usage === 'Mobile' ? '(mobile)' : p.usage === 'Home' ? '(dom.)' : p.usage === 'Work' ? '(trav.)' : `(${p.usage})`}</span>}
                              </div>
                            ))
                          : (client?.Phone || '—')}
                      </td>
                      <td style={{ fontSize: '12px' }}>{client?.Email || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}

      {loading && (
        <div style={{ textAlign: 'center', padding: '20px 0', color: 'var(--text-secondary)' }}>
          🔄 Recherche en cours...
        </div>
      )}

      {!loading && searched && totalMatches === 0 && (
        <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-muted)' }}>
          Aucun résultat pour "{query}"
        </div>
      )}
    </div>
  );
}

export default NextlaneSearch;
